import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';
import '../luna-icon/luna-icon';

type ButtonVariant = 'primary' | 'success' | 'neutral' | 'checkpoint' | 'hatched';
type ButtonSize = 'md' | 'xl' | 'secondary';

const VARIANTS: readonly ButtonVariant[] = [
  'primary',
  'success',
  'neutral',
  'checkpoint',
  'hatched',
];

const SIZES: readonly ButtonSize[] = ['md', 'xl', 'secondary'];

@customElement('luna-button')
export class LunaButton extends LitPage {
  private _variant: ButtonVariant = 'primary';

  @property({ attribute: 'variant' })
  get variant(): ButtonVariant {
    return this._variant;
  }

  set variant(value: string | null) {
    const next = String(value ?? '');
    if (!(VARIANTS as readonly string[]).includes(next)) {
      devWarn(`luna-button: unknown variant "${next}" — falling back to "primary"`);
      this._variant = 'primary';
      return;
    }
    this._variant = next as ButtonVariant;
    if (this._size === 'secondary' && !this.isHatched) {
      devWarn(
        `luna-button: size "secondary" is only valid for the hatched variant — falling back to "md"`
      );
      this._size = 'md';
    }
  }

  private _size: ButtonSize = 'md';

  @property({ attribute: 'size' })
  get size(): ButtonSize {
    return this._size;
  }

  set size(value: string | null) {
    const next = String(value ?? '');
    if (!(SIZES as readonly string[]).includes(next)) {
      devWarn(`luna-button: unknown size "${next}" — falling back to "md"`);
      this._size = 'md';
      return;
    }
    if (next === 'secondary' && !this.isHatched) {
      devWarn(
        `luna-button: size "secondary" is only valid for the hatched variant — falling back to "md"`
      );
      this._size = 'md';
      return;
    }
    this._size = next as ButtonSize;
  }

  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) full = false;
  @property({ attribute: 'icon' }) icon = '';
  @property({ attribute: 'label2' }) label2 = '';

  private get isHatched(): boolean {
    return this._variant === 'hatched';
  }

  private effectiveSize(): 'md' | 'xl' | 'secondary' {
    if (this.isHatched) {
      return this._size === 'xl' ? 'xl' : 'secondary';
    }
    return this._size === 'xl' ? 'xl' : 'md';
  }

  content() {
    const hatched = this.isHatched;
    const size = this.effectiveSize();
    const nonInteractive = hatched || this.disabled;

    return html`
      <button
        type="button"
        class="luna-button luna-button--${this._variant} luna-button--${size} ${this.full ? 'luna-button--full' : ''}"
        ?disabled=${this.disabled}
        aria-disabled=${nonInteractive ? 'true' : 'false'}
        @click=${this.onClickGuard}
      >
        ${hatched ? this.hatchedContent() : this.standardContent(size)}
      </button>
    `;
  }

  // aria-disabled does not block native activation: suppress the click so a
  // hatched (locked) button can't bubble a click into host-level listeners.
  private onClickGuard(event: Event): void {
    if (this.isHatched || this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private standardContent(size: 'md' | 'xl' | 'secondary') {
    const iconSize = size === 'xl' ? 24 : 22;
    const labelClass = size === 'xl' ? 'mbw-type-label-button-xl' : 'mbw-type-label-button';

    return html`
      ${this.icon
        ? html`<luna-icon name=${this.icon} size=${iconSize} aria-hidden="true"></luna-icon>`
        : ''}
      <span class="luna-button__label ${labelClass}"><slot></slot></span>
    `;
  }

  private hatchedContent() {
    return html`
      <luna-icon name="lock" size="18" aria-hidden="true"></luna-icon>
      <span class="luna-button__stack">
        <span class="luna-button__label luna-button__label--hatched"><slot></slot></span>
        ${this.label2
          ? html`<span class="luna-button__label2">${this.label2}</span>`
          : ''}
      </span>
    `;
  }

  styles() {
    return css`
      .luna-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-sizing: border-box;
        border: none;
        cursor: pointer;
        font-family: var(--mbw-font-sans);
        background-image: var(--luna-btn-fill);
        box-shadow: var(--luna-btn-shadow);
      }

      .luna-button:not(.luna-button--hatched):hover {
        background-image: linear-gradient(
            var(--luna-btn-hover-overlay),
            var(--luna-btn-hover-overlay)
          ),
          var(--luna-btn-fill);
      }

      .luna-button:active {
        box-shadow: var(--luna-btn-shadow-active, var(--luna-btn-shadow));
      }

      .luna-button:focus-visible {
        outline: none;
        box-shadow:
          var(--luna-btn-shadow),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-button:disabled:not(.luna-button--hatched) {
        opacity: 0.4;
        cursor: default;
        box-shadow: none;
      }

      .luna-button--hatched {
        cursor: default;
      }

      .luna-button__label {
        color: inherit;
      }

      .luna-button__stack {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 1px;
        min-width: 0;
        text-align: left;
      }

      .luna-button__label--hatched {
        font-weight: 700;
        font-size: 15px; /* pinned: figma Hatched Button */
        line-height: 20px;
      }

      .luna-button__label2 {
        font-weight: 400;
        font-size: 12px; /* pinned: figma Hatched Button */
        line-height: 16px;
      }

      /* Sizes */
      .luna-button--md {
        width: 180px;
        height: 56px;
        border-radius: var(--luna-radius-btn);
        padding: 0 24px;
        gap: 8px;
      }

      .luna-button--xl {
        width: 260px;
        height: 56px;
        border-radius: var(--luna-radius-btn-xl);
        padding: 0 28px;
        gap: 10px;
      }

      .luna-button--secondary {
        width: 200px;
        height: 56px;
        border-radius: var(--luna-radius-btn);
        padding: 0 14px;
        gap: 10px;
      }

      .luna-button--hatched.luna-button--xl {
        width: 200px;
        border-radius: var(--luna-radius-btn-xl);
      }

      /* Variants */
      .luna-button--primary {
        --luna-btn-fill: var(--mbw-btn-primary-fill); /* themed per data-theme (BR-93); blue value pinned: figma Luna Button */
        --luna-btn-shadow: inset 0 1px 0 #ffffff, 0 3px 0 var(--mbw-btn-primary-ledge);
        --luna-btn-shadow-active: inset 0 1px 0 #ffffff, 0 1px 0 var(--mbw-btn-primary-ledge);
        --luna-btn-hover-overlay: rgba(255, 255, 255, 0.07);
        color: var(--cds-text-on-color);
      }

      .luna-button--success {
        --luna-btn-fill: linear-gradient(180deg, #4a8a47 0%, #3d7a3d 25%, #386f38 100%); /* pinned: figma Luna Button */
        --luna-btn-shadow: inset 0 1px 0 #ffffff, 0 3px 0 var(--luna-edge-green);
        --luna-btn-shadow-active: inset 0 1px 0 #ffffff, 0 1px 0 var(--luna-edge-green);
        --luna-btn-hover-overlay: rgba(255, 255, 255, 0.07);
        color: var(--cds-text-on-color);
      }

      .luna-button--neutral {
        --luna-btn-fill: linear-gradient(180deg, #ffffff 0%, #f1f3f6 100%); /* pinned: figma Luna Button */
        --luna-btn-shadow: inset 0 0 0 2px var(--luna-outline-neutral), 0 3px 0 var(--luna-outline-neutral);
        --luna-btn-shadow-active: inset 0 1px 0 #ffffff, 0 1px 0 var(--luna-outline-neutral);
        --luna-btn-hover-overlay: rgba(0, 0, 0, 0.03);
        color: var(--luna-outline-neutral-text);
      }

      .luna-button--checkpoint {
        --luna-btn-fill: linear-gradient(180deg, #ffffff 0%, #eef3fb 100%); /* pinned: figma Luna Button */
        --luna-btn-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint), 0 3px 0 var(--luna-outline-checkpoint-ledge);
        --luna-btn-shadow-active: inset 0 1px 0 #ffffff, 0 1px 0 var(--luna-outline-checkpoint);
        --luna-btn-hover-overlay: rgba(0, 0, 0, 0.03);
        color: var(--luna-outline-checkpoint-text);
      }

      .luna-button--hatched {
        --luna-btn-fill: repeating-linear-gradient(
          135deg,
          var(--luna-hatch-light) 0px,
          var(--luna-hatch-light) 7px,
          var(--luna-hatch-dark) 7px,
          var(--luna-hatch-dark) 14px
        );
        --luna-btn-shadow: inset 0 0 0 2px var(--luna-hatch-border),
          inset 0 2px 4px rgba(0, 0, 0, 0.15); /* pinned: figma Hatched Button */
        color: var(--luna-hatch-text);
      }

      /* Full-width opt-in (e.g. the resume card's Continue, which fills the
         card's inner width in figma Home). Placed after the size rules so it
         wins the specificity tie. */
      .luna-button--full {
        width: 100%;
      }

      /* XL ambient glow — only the primary XL variant is drawn in Figma (the
         XL component shows the primary gradient + ledge y4 + blue glow). */
      .luna-button--xl.luna-button--primary {
        --luna-btn-shadow: inset 0 1px 0 #ffffff, 0 4px 0 var(--mbw-btn-primary-ledge),
          0 8px 16px var(--mbw-btn-primary-glow); /* themed per data-theme (BR-93); blue value pinned: figma Luna Button XL */
        --luna-btn-shadow-active: inset 0 1px 0 #ffffff, 0 1px 0 var(--mbw-btn-primary-ledge);
      }
    `;
  }
}
